import type {
  LBNodeData,
  TextTemplateChunk,
  VariableRef,
} from '@/proto/logiblocks/logiblocks';
import { VariableSource, VariableType, VariableDesc } from '@/proto/common';
import { NodeVariableScope, type NodeOutput } from '@/proto/variable_schema';
import {
  getLBNodeInfo,
  getNodeDisplayName,
  type NodeType,
} from '../nodes/registry';
import type { ReactFlowEdge, ReactFlowNode } from '../types';
import { getAncestorNodes } from '../graphTraversal';
import { insertNewChildRaw } from '../graphUtils';
import { getDeclaredOutputs, getNodeTypeOutputs } from './variableDefinitions';
import { getSystemVariable } from './systemVariableCatalog';
import {
  displayNameToSnakeCase,
  getAvailableVariables,
  isSystemRef,
  lookupVariableRef,
  snakeCaseToDisplayName,
  variableRefName,
  type AncestorNodeContext,
} from './variableRefs';

/**
 * Checks if a node is inside a given subgraph parent's subgraph.
 * Traverses up the parentId chain to check containment.
 */
function isNodeInsideSubgraph(
  node: ReactFlowNode | undefined,
  subgraphParentId: string,
  nodes: ReactFlowNode[]
): boolean {
  let current = node;
  while (current) {
    if (current.parentId === subgraphParentId) {
      return true;
    }
    current = nodes.find((n) => n.id === current?.parentId);
  }
  return false;
}

/**
 * The variables the workflow author declared on a node: its user_variables,
 * and on an AI call node the pca_variable_names saved before collected
 * variables became user variables.
 */
function getUserOutputVariables(
  nodeType: NodeType,
  nodeData: LBNodeData
): NodeOutput[] {
  const outputs = (nodeData.user_variables ?? []).map((uv): NodeOutput => ({
    variable: uv,
    scope: NodeVariableScope.GLOBAL,
    published_when: [],
    source: VariableSource.SOURCE_USER,
  }));

  // A node saved by an older editor lists a collected variable in
  // pca_variable_names as "Tracking Number". Once the author saves it again in
  // this editor, user_variables holds the same variable as tracking_number, and
  // the picker must list it once. Older nodes also list "Caller Name" there
  // while the node type publishes caller_name itself. The backend skips both
  // kinds of entry in AICallActionNode.get_output_variables, and this mirrors it.
  //
  // TODO(tim): delete the pca_variable_names branch once no stored node lists
  // any.
  const publishedNames = new Set(
    [...getNodeTypeOutputs(nodeType, nodeData), ...outputs].map(
      (output) => output.variable!.name
    )
  );
  const pcaNames: string[] = nodeData.ai_call_action?.pca_variable_names ?? [];
  for (const name of pcaNames) {
    const snakeName = displayNameToSnakeCase(name);
    if (publishedNames.has(snakeName)) {
      continue;
    }
    publishedNames.add(snakeName);
    outputs.push({
      variable: VariableDesc.fromPartial({
        name: snakeName,
        display_name: snakeCaseToDisplayName(name),
        type: VariableType.STRING,
      }),
      scope: NodeVariableScope.GLOBAL,
      published_when: [],
      source: VariableSource.SOURCE_USER,
    });
  }
  return outputs;
}

/**
 * Every variable a node publishes: its type's outputs, given how it's set up,
 * then the ones the workflow author declared. A user variable with the same
 * name as a node output is listed too, under its own source;
 * validateUserVariables rejects the collision at publish.
 */
function getNodeOutputVariables(
  nodeType: NodeType,
  nodeData: LBNodeData
): NodeOutput[] {
  return [
    ...getNodeTypeOutputs(nodeType, nodeData),
    ...getUserOutputVariables(nodeType, nodeData),
  ];
}

/**
 * Gets all output variables published by a node's ancestors.
 * Variables with scope 'subgraph' are only included if the requesting node
 * is inside that ancestor's subgraph.
 *
 * @param nodeId - The ID of the node to find ancestor variables for
 * @param nodes - All nodes in the graph
 * @param edges - All edges in the graph
 * @returns Array of NodeVariables, one for each ancestor that publishes visible variables
 */
export function getAncestorVariables(
  nodeId: string,
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): AncestorNodeContext[] {
  const requestingNode = nodes.find((n) => n.id === nodeId);
  const ancestors = getAncestorNodes(nodeId, nodes, edges);
  const result: AncestorNodeContext[] = [];

  for (const ancestor of ancestors) {
    const nodeData = ancestor.data as LBNodeData;
    const nodeInfo = getLBNodeInfo(nodeData);
    if (!nodeInfo) continue;

    const nodeOutputs = getNodeOutputVariables(
      nodeInfo.protobufNodeDataKey,
      nodeData
    );

    if (nodeOutputs.length > 0) {
      // Check once whether requesting node is inside this ancestor's subgraph
      const isInsideAncestorSubgraph = isNodeInsideSubgraph(
        requestingNode,
        ancestor.id,
        nodes
      );

      // Filter variables based on scope. 'subgraph' variables can only be seen
      // if this node is a member of the ancestor's subgraph (as opposed to
      // being a regular descendant of the ancestor, in the same graph)
      const visibleVariables: NodeOutput[] = [];
      for (const output of nodeOutputs) {
        if (
          output.scope === NodeVariableScope.SUBGRAPH &&
          !isInsideAncestorSubgraph
        ) {
          continue;
        }
        visibleVariables.push(output);
      }

      if (visibleVariables.length > 0) {
        result.push({
          nodeId: ancestor.id,
          nodeInfo,
          nodeDisplayName: getNodeDisplayName(nodeData, nodeInfo),
          filteredOutputVariables: visibleVariables,
        });
      }
    }
  }

  return result;
}

/**
 * Formats a variable reference as a human-readable string.
 * 'default' includes the node label prefix (e.g. "Alert: Shipment Status").
 * 'short' returns just the variable name (e.g. "Shipment Status").
 */
export function formatVariableRef(
  ref: VariableRef | undefined,
  nodes: ReactFlowNode[],
  format: 'default' | 'short' = 'default'
): string {
  if (!ref || !ref.node_id) {
    return '?';
  }

  // The name as the author wrote it, used wherever nothing better resolves.
  const fullName = variableRefName(ref);

  // System variables have no publishing node, so their labels come from the catalog.
  if (isSystemRef(ref)) {
    const sysVar = getSystemVariable(fullName);
    const label = sysVar?.displayName ?? fullName;
    return format === 'short' ? label : `System: ${label}`;
  }

  // A reference to a node that's gone, or to one whose type we can't read, keeps
  // its name and loses the label prefix.
  const node = nodes.find((n) => n.id === ref.node_id);
  if (!node) {
    if (format === 'short') {
      return fullName;
    }
    return `?: ${fullName}`;
  }

  const nodeInfo = getLBNodeInfo(node.data as LBNodeData);
  if (!nodeInfo) {
    if (format === 'short') {
      return fullName;
    }
    return `?: ${fullName}`;
  }

  // The node type's outputs are searched first. The author's variables are
  // searched only if the name isn't among them, because getUserOutputVariables
  // allocates a new list on every call.
  const nodeType = nodeInfo.protobufNodeDataKey;
  const nodeData = node.data as LBNodeData;
  let found = lookupVariableRef(ref, getDeclaredOutputs(nodeType));
  if (!found) {
    found = lookupVariableRef(ref, getUserOutputVariables(nodeType, nodeData));
  }

  // A derived variable is labelled from the meta-variable catalog.
  if (found?.kind === 'derived') {
    const label = found.derived.displayName;
    return format === 'short' ? label : `${nodeInfo.label}: ${label}`;
  }

  // A name nothing publishes is shown as written, rather than humanized into a
  // label that reads like a real variable.
  if (!found) {
    return format === 'short' ? fullName : `${nodeInfo.label}: ${fullName}`;
  }

  // A published variable uses its declared label, falling back to its name.
  const rawLabel = found.variable.display_name || found.variable.name;
  const varLabel = snakeCaseToDisplayName(rawLabel);
  if (format === 'short') {
    return varLabel;
  }
  return `${nodeInfo.label}: ${varLabel}`;
}

/**
 * Returns a template's chunks as display text: text as written, each variable
 * as its short label, and each mention as `@label`.
 */
export function formatTemplateChunks(
  chunks: TextTemplateChunk[],
  nodes: ReactFlowNode[]
): string {
  const parts: string[] = [];
  for (const chunk of chunks) {
    if (chunk.text !== undefined) {
      parts.push(chunk.text);
    } else if (chunk.variable !== undefined) {
      parts.push(formatVariableRef(chunk.variable, nodes, 'short'));
    } else if (chunk.user_mention !== undefined) {
      parts.push(`@${chunk.user_mention.label}`);
    } else {
      console.error('Template chunk has no part set', chunk);
    }
  }
  return parts.join('');
}

/**
 * Gets the type of a variable from a variable reference.
 * Looks up the node that publishes the variable and returns its type.
 *
 * @param ref - The variable reference to look up
 * @param nodes - All nodes in the graph
 * @returns The variable's VariableType, or undefined if node or variable not found
 */
export function getVariableType(
  ref: VariableRef,
  nodes: ReactFlowNode[]
): VariableType | undefined {
  if (isSystemRef(ref)) {
    // Synthetic system variable. None has fields, so system.now.foo names nothing.
    return getSystemVariable(variableRefName(ref))?.type;
  }

  const node = nodes.find((n) => n.id === ref.node_id);
  if (!node) return undefined;

  const nodeInfo = getLBNodeInfo(node.data as LBNodeData);
  if (!nodeInfo) return undefined;

  // The node type's outputs are searched first. The author's variables are
  // searched only if the name isn't among them, because getUserOutputVariables
  // allocates a new list on every call.
  const nodeType = nodeInfo.protobufNodeDataKey;
  const nodeData = node.data as LBNodeData;
  let found = lookupVariableRef(ref, getDeclaredOutputs(nodeType));
  if (!found) {
    found = lookupVariableRef(ref, getUserOutputVariables(nodeType, nodeData));
  }
  if (!found) return undefined;
  return found.kind === 'variable' ? found.variable.type : found.derived.type;
}

/**
 * Reports whether a `childType` node, if added as a child of `parentNodeId`
 * right now, would have a SHIPMENT-typed variable available from its
 * ancestors. Used by the node picker to disable node types that need a
 * shipment in context (e.g. CoC / alert-status actions) before the user
 * configures an uncompletable node.
 *
 * Simulates the insertion with `insertNewChildRaw` (without touching the
 * real graph) so subgraph membership and edge wiring exactly match what
 * would actually happen on selection.
 */
export function wouldChildSeeShipmentVariable(
  parentNodeId: string,
  childType: NodeType,
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): boolean {
  const parentNode = nodes.find((n) => n.id === parentNodeId);
  if (!parentNode) return false;

  const inserted = insertNewChildRaw(parentNode, childType, nodes, edges);
  if (!inserted) return false;

  const ancestorVariables = getAncestorVariables(
    inserted.childNode.id,
    inserted.nodes,
    inserted.edges
  );
  return (
    getAvailableVariables(ancestorVariables, [VariableType.SHIPMENT]).length > 0
  );
}
